import { FieldModel, UserId } from '@cibo/core'
import { Loading, useOrgUser } from '@cibo/profile'
import { Stack } from '@mui/material'
import { StatusCodes } from 'http-status-codes'
import { ADMIN_ROUTES } from 'navigation/types'
import ROUTE, { ENROLLMENT_ROUTES, PROGRAM_ROUTES } from 'navigation/types/routes'
import { AddFields } from 'pages/LandManager/Add'
import { useColumnSets } from 'pages/LandManager/useColumnSets'
import { generatePath, useNavigate } from 'react-router-dom'
import { FieldTable } from '../../components'
import { ProfileSelectedFieldsButton } from '../../components/ProfileSelectedFieldsButton'
import { FullScreenError, useParamAsInteger } from '../../consts/Theme'
import { ENGAGEMENT_PATHS } from '../../navigation/types/engagementRoutes'
import { useConfirmOpportunityDialog } from '../LandManager/useConfirmOpportunity'

export const UserFields = () => {
  const userId = useParamAsInteger('userId')
  const navigate = useNavigate()

  const user = useOrgUser(userId)

  const { confirmOpportunityDialog, setConfirmOpportunity } = useConfirmOpportunityDialog()

  const onClickNeedsInfo = (field: FieldModel) =>
    navigate(generatePath(ROUTE.PROFILE_FIELD, { fieldId: field.resourceId }))

  const { userFieldColumns } = useColumnSets({
    onClickNeedsInfo,
    onClickOpportunity: ({ programId, field }) => setConfirmOpportunity({ programId, field }),
    onClickEngaged: ({
      engagementId,
      programId,
      userId,
    }: {
      engagementId: string
      programId: string
      userId: UserId
    }) => {
      navigate(
        generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
          engagementId,
          programId,
        })
      )
    },
    onClickEnrolling: ({ workflowId }: { workflowId: string }) => {
      navigate(generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId }))
    },
    onClickProgram: (programId: string) =>
      navigate(generatePath(PROGRAM_ROUTES.DETAIL, { programId })),
  })

  if (!userId) {
    return <FullScreenError status={StatusCodes.NOT_FOUND} />
  }
  if (!!user.isLoading) {
    return <Loading />
  }

  return (
    <>
      {!!user.data?.user.resources ? (
        <FieldTable
          columns={userFieldColumns}
          baseQuery={{ owner: userId }}
          paths={{
            draw: generatePath(ADMIN_ROUTES.USER_FIELDS_DRAW, { userId: userId.toString() }),
            upload: generatePath(ADMIN_ROUTES.USER_FIELDS_UPLOAD, { userId: userId.toString() }),
          }}
          components={{
            BulkActions: props => (
              <Stack direction="row" alignItems="center" spacing={2}>
                <ProfileSelectedFieldsButton variant="contained" size="small" color="warning" />
              </Stack>
            ),
          }}
        />
      ) : (
        <AddFields
          paths={{
            draw: generatePath(ADMIN_ROUTES.USER_FIELDS_DRAW, { userId: userId.toString() }),
            upload: generatePath(ADMIN_ROUTES.USER_FIELDS_UPLOAD, { userId: userId.toString() }),
          }}
          userId={userId}
        />
      )}
      {confirmOpportunityDialog}
    </>
  )
}
